import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { 
  Users, ChartLineUp, Package, Browser, Envelope, Play,
  Lightning, TrendUp, Fire, CalendarBlank, ArrowLeft
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { getUser } from '@/utils/auth';
import api from '@/utils/api';
import { toast } from 'sonner';

const AdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const [user] = useState(getUser());
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is admin
    if (!user?.email?.includes('admin')) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAnalytics();
  }, [user, navigate]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load analytics');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-white/10 rounded w-1/4"></div>
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-white/10 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const mainStats = [
    { 
      label: 'Total Users', 
      value: analytics.total_users, 
      icon: Users, 
      color: 'from-blue-500 to-cyan-500',
      description: 'All registered users'
    },
    { 
      label: 'Active (7 days)', 
      value: analytics.active_users_7_days, 
      icon: Fire, 
      color: 'from-orange-500 to-red-500',
      description: 'Users active in last 7 days'
    },
    { 
      label: 'Active (30 days)', 
      value: analytics.active_users_30_days, 
      icon: TrendUp, 
      color: 'from-emerald-500 to-teal-500',
      description: 'Users active in last 30 days'
    },
    { 
      label: 'Events Logged', 
      value: analytics.total_events_logged, 
      icon: Lightning, 
      color: 'from-purple-500 to-pink-500',
      description: 'Total activity events'
    },
  ];

  const contentStats = [
    { label: 'Content Created', value: analytics.total_content_created, icon: Play, color: 'text-purple-400' },
    { label: 'Videos Generated', value: analytics.total_videos_generated, icon: Play, color: 'text-pink-400' },
    { label: 'Offers Created', value: analytics.total_offers_created, icon: Package, color: 'text-amber-400' },
    { label: 'Sales Pages', value: analytics.total_sales_pages_created, icon: Browser, color: 'text-blue-400' },
    { label: 'Email Campaigns', value: analytics.total_email_campaigns_created, icon: Envelope, color: 'text-emerald-400' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-slate-400 hover:text-white mb-2 transition-colors"
            >
              <ArrowLeft size={16} className="mr-1" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-white">Platform Analytics</h1>
            <p className="text-slate-400">Product intelligence and usage metrics</p>
          </div>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {mainStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                    <Icon size={20} className="text-white" weight="fill" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value.toLocaleString()}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
                <div className="text-xs text-slate-500 mt-1">{stat.description}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Content Stats */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Content & Monetization</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {contentStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="bg-white/5 rounded-lg p-4 text-center">
                  <Icon size={24} weight="fill" className={`${stat.color} mx-auto mb-2`} />
                  <div className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</div>
                  <div className="text-xs text-slate-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Events by Type */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Events by Type</h2>
            {Object.keys(analytics.events_by_type).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.events_by_type).map(([eventType, count]) => (
                  <div key={eventType} className="flex items-center justify-between">
                    <span className="text-slate-300 text-sm font-mono">
                      {eventType.replace(/_/g, ' ')}
                    </span>
                    <span className="text-white font-bold">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No events logged yet</p>
            )}
          </div>

          {/* Daily Signups */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Daily Signups (7 days)</h2>
              <CalendarBlank size={20} className="text-slate-400" />
            </div>
            <div className="space-y-2">
              {analytics.daily_signups_7_days.map((day) => {
                const maxSignups = Math.max(...analytics.daily_signups_7_days.map(d => d.signups), 1);
                const percentage = (day.signups / maxSignups) * 100;
                return (
                  <div key={day.date} className="flex items-center space-x-3">
                    <span className="text-slate-400 text-xs w-20">{day.date}</span>
                    <div className="flex-1 bg-white/10 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-white font-bold text-sm w-8 text-right">{day.signups}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Niches */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Top Niches</h2>
            {analytics.top_niches.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_niches.slice(0, 5).map((item, index) => (
                  <div key={item.niche} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-500 text-sm">#{index + 1}</span>
                      <span className="text-slate-300">{item.niche}</span>
                    </div>
                    <span className="text-white font-bold">{item.count} users</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No niche data available</p>
            )}
          </div>

          {/* Top Platforms */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Top Platforms</h2>
            {analytics.top_platforms.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_platforms.slice(0, 5).map((item, index) => (
                  <div key={item.platform} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-500 text-sm">#{index + 1}</span>
                      <span className="text-slate-300">{item.platform}</span>
                    </div>
                    <span className="text-white font-bold">{item.count} users</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No platform data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
